import React, { useState } from 'react'
import ModalInc from '../../../../../shared-ui/ModalInc'
import ButtonInc from '../../../../../shared-ui/ButtonInc'
import { TextValidator, SelectValidator, ValidatorForm } from 'react-material-ui-form-validator';
import MenuItem from '@mui/material/MenuItem';
import AlertDialog from '../../../../../components/AlertDialog/index.es6';
import { CircularProgress } from '@mui/material';
import { useStyle } from "./Style";
import { Grid } from '@mui/material'
import { getPartnerGuid } from '../../../../../lib/access.es6';
import {
  payableForClaim,
  splitPayout,
  payoutTranches,
  bookableTranches,
  trancheReference
} from '../../../../../lib/claimBenefitCaps.es6';

const AddFuneralClaimExpense = (props) => {
  const classes = useStyle();
  const partnerGuid = getPartnerGuid();
  const isAmountEditable = partnerGuid === 'old_mutual_malawi' || partnerGuid === 'samba';

  const [expenseType, setExpenseType] = useState('')
  const [amountInCents, setAmountInCents] = useState('');

  // The claims-time caps, applied to what is actually bookable here. Nothing
  // enforced them before, so a child of 8 or a member declared with a condition in
  // their first year of cover could be booked the full tier (retest items 17, 18),
  // and a KES 500,000 claim could be booked in full with no death certificate on
  // file (retest item 15).
  //
  // One derivation, shared with the required-document checklist so the two cannot
  // disagree about what this claim pays.
  const policy = props.policy || {};
  const cap = payableForClaim(props.claim, policy, expenseType);

  // A death certificate is what releases the second tranche of a split payout.
  const documents = props.claim.claim_documents || [];
  const hasDeathCertificate = documents.some(function (d) {
    const label = (d && (d.description || d.document_type || d.name)) || '';
    return /death\s*cert/i.test(String(label));
  });
  const tranche = splitPayout(cap.payable, hasDeathCertificate);
  const maxBookable = tranche.releasableNow;

  // A payout above KES 250,000 is not one invoice for the whole amount: it is two —
  // the first KES 250,000 and the balance (so KES 500,000 books as 250,000 +
  // 250,000). Only OMK raises the split; the partners that can edit the amount by
  // hand keep booking whatever the assessor types.
  const isSplitPartner = partnerGuid === 'omk';
  const plannedTranches = isSplitPartner
    ? payoutTranches(cap.payable, hasDeathCertificate)
    : [];

  // What this claim has already booked, by tranche reference. Posting a tranche is
  // not atomic across two invoices: the first can commit and the second fail, and
  // the assessor then saves again from the same dialog. Without this, that rebuilt
  // both payloads and booked tranche 1 twice — KES 750,000 against a KES 500,000
  // claim. Reading what is on the record keeps a retry raising only what is left.
  const bookedReferences = (props.claim.benefit_usages || [])
    .map(function (usage) {
      return usage && usage.external_unique_identifier;
    })
    .filter(Boolean);
  const isAlreadyBooked = (tranche) =>
    bookedReferences.indexOf(trancheReference(props.claim.guid, tranche.number)) !== -1;

  const releasable = isSplitPartner
    ? bookableTranches(cap.payable, hasDeathCertificate)
    : [];
  const invoicesToRaise = releasable.filter(function (t) {
    return !isAlreadyBooked(t);
  });
  const alreadyBookedCount = releasable.length - invoicesToRaise.length;
  const willSplit = releasable.length > 1;
  // Every releasable tranche is already on the claim, so Save has nothing to do.
  const nothingToRaise = willSplit && invoicesToRaise.length === 0;

  const reset = () => {
    setAmountInCents('');
    setExpenseType('');
  }
  const buildPayload = (amountInKes, reference) => ({
    "benefit_type": expenseType,
    // "invoice_amount_in_cents": Number(invoice_amount_in_cents),
    "benefit_amount_in_cents": Math.floor(Number(amountInKes) * 100),
    "benefit_usage_count": 0,
    // "invoice_number": invoiceNumber,
    // Only a split payout carries a reference. This field already means the death
    // certificate number elsewhere in this app (AddHospitalClaimExpense) and is
    // rendered back as the invoice number, so the single-invoice path keeps sending
    // null exactly as it always has.
    "external_unique_identifier": reference || null,
    "claim_guid": props.claim.guid
  })
  const handleClaimExpense = () => {
    // Nothing outstanding means nothing to post. Sending an empty list would report
    // "Claim expense created" over a claim that gained nothing.
    if (nothingToRaise) {
      return
    }
    // Raise one invoice per tranche still outstanding. Below the threshold — and for
    // every partner that is not OMK — that is exactly the single invoice it always was.
    const payload = willSplit
      ? invoicesToRaise.map(function (t) {
        return buildPayload(t.amount, trancheReference(props.claim.guid, t.number));
      })
      : buildPayload(amountInCents);
    props.handleClaimExpense(payload)
    reset()
  }
  const handleExpenseCreatedSuccessfully = () => {
    props.resetCreateExpenseSuccessAlert();
    props.toggleClaimExpenseDialog();
    window.location.reload();
  }
  const handleExpenseCreateError = () => {
    props.resetCreateExpenseErrorAlert();
  }



  const handleChange = (event) => {
    const { name, value } = event.target

    if (name === "amountInCents") {
      setAmountInCents(value)
    }
    if (name === "selectexpensetype") {
      setExpenseType(value);
      const capped = payableForClaim(props.claim, policy, value);
      if (capped.benefit) {
        // Propose the releasable amount, not the raw registered cover.
        setAmountInCents(splitPayout(capped.payable, hasDeathCertificate).releasableNow);
      } else {
        setAmountInCents('');
      }
    }
  }
  return (
    <>
      <AlertDialog
        custom
        show={props.showCreateExpenseProgressAlert}
        size="sm"
        style={{ marginTop: '0', top: '30vh' }}
        confirmBtnText={'ok'}
        showCancel={false}
        showConfirm={false}
        cancelBtnText={'cancel'}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2> Creating Claim Expense...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.showCreateExpenseSuccessAlert}
        size="sm"
        title={'Claim expense created'}
        style={{ marginTop: '0', top: '30vh' }}
        onConfirm={() => { handleExpenseCreatedSuccessfully() }}
        confirmBtnText={'ok'}
        showCancel={false}
        showConfirm={true}
        cancelBtnText={'cancel'}
        confirmBtnCssClass
      >

      </AlertDialog>

      <AlertDialog
        show={props.showCreateExpenseErrorAlert}
        danger
        title={'Error creating claim expense'}
        onConfirm={() => handleExpenseCreateError()}
        confirmBtnText={'Try again'}
        confirmBtnCssClass
        showCancel={false}
        style={{ marginTop: '0', top: '30vh' }}
      >

        {props.CreateExpenseError ? props.CreateExpenseError.message : ''}
      </AlertDialog>
      <ModalInc
        modalTitle="Input  Details "
        subTitle="Fill the below fields to add details"
        open={props.openClaimExpenseDialog}
        onClose={props.toggleClaimExpenseDialog}
        fullWidth

      >
        <ValidatorForm onSubmit={() => handleClaimExpense()} >
          <Grid container spacing={2} className={classes.editClaimDialog}>
            <Grid item xs={12} sm={12} md={12}>
              <SelectValidator
                labelId="select-expense-type"
                id="select-expense-type"
                value={expenseType}
                name='selectexpensetype'
                type="text"
                label="Select  Type"
                onChange={handleChange}
                variant="outlined"
                validators={['required']}
                errorMessages={['Please select type']}
                style={{ width: "100%" }}
                sx={{
                  fontFamily: "Open Sans",
                  fontStyle: "normal",
                  fontWeight: 300,
                  fontSize: "12px",
                  lineHeight: "16px",
                  letterSpacing: "0.005em",
                  color: "#000000",
                }}
              >

                {
                  props.claim.benefits.map((benefit, index) => (
                    <MenuItem
                      key={index}
                      sx={{
                        fontFamily: "Open Sans",
                        fontStyle: "normal",
                        fontWeight: 300,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.005em",
                        color: "#000000",
                      }}
                      value={benefit.type}>{benefit.type}</MenuItem>
                  ))
                }

              </SelectValidator>
            </Grid>



            <Grid item xs={12} sm={12} md={12}>
              <TextValidator
                name='amountInCents'
                id="amountInCents"
                label="Amount Payable"
                disabled={!isAmountEditable}
                type="number"
                value={amountInCents}
                onChange={handleChange}
                fullWidth
                variant="outlined"
                validators={
                  isAmountEditable
                    ? ['required', 'minNumber:0.01'].concat(
                      maxBookable > 0 ? ['maxNumber:' + maxBookable] : []
                    )
                    : ['required']
                }
                errorMessages={
                  isAmountEditable
                    ? ['Please enter Amount', 'Amount must be greater than zero'].concat(
                      maxBookable > 0
                        ? ['Amount cannot exceed KES ' + maxBookable.toLocaleString() + ' on this claim']
                        : []
                    )
                    : ['Please enter Amount']
                }
              />
            </Grid>

            {/* Say why the amount is what it is, so an assessor is not left guessing
                whether a lower figure is a mistake (retest items 15, 17, 18). */}
            {expenseType && cap.capped && (
              <Grid item xs={12} sm={12} md={12}>
                <p style={{ margin: 0, fontSize: '12px', color: '#8a5300' }}>
                  Registered cover is KES {cap.registered.toLocaleString()}; KES{' '}
                  {cap.payable.toLocaleString()} is payable. {cap.reason}.
                </p>
              </Grid>
            )}
            {expenseType && tranche.split && (
              <Grid item xs={12} sm={12} md={12}>
                <p style={{ margin: 0, fontSize: '12px', color: hasDeathCertificate ? '#2e6b32' : '#8F191C' }}>
                  {tranche.note}
                </p>
              </Grid>
            )}

            {/* A split payout raises two invoices, so say so before it happens —
                otherwise Save silently produces a different number of rows depending
                on whether the death certificate is on file. */}
            {expenseType && isSplitPartner && plannedTranches.length > 1 && (
              <Grid item xs={12} sm={12} md={12}>
                <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#000' }}>
                  {invoicesToRaise.length === 0
                    ? 'Nothing left to raise on this claim:'
                    : 'Saving will raise ' +
                      invoicesToRaise.length +
                      ' of ' +
                      plannedTranches.length +
                      (invoicesToRaise.length === 1 ? ' invoices:' : ' invoices:')}
                </p>
                <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                  {plannedTranches.map((t, index) => {
                    // Three states, and an assessor returning after a partial failure
                    // needs to tell them apart: already on the claim, about to be
                    // raised, or waiting on the death certificate.
                    const booked = isAlreadyBooked(t);
                    const colour = booked
                      ? '#8D8D8C'
                      : t.heldBack
                        ? '#8F191C'
                        : '#2e6b32';
                    return (
                      <li key={index} style={{ fontSize: '12px', color: colour }}>
                        {t.label}: KES {t.amount.toLocaleString()}
                        {booked
                          ? ' — already raised, will not be booked again'
                          : t.heldBack
                            ? ' — not raised yet'
                            : ''}
                      </li>
                    );
                  })}
                </ul>
                {alreadyBookedCount > 0 && (
                  <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#8a5300' }}>
                    This claim already carries {alreadyBookedCount} of its invoices.
                  </p>
                )}
              </Grid>
            )}

          </Grid>
          <Grid container spacing={2} >
            <Grid item xs={12} sm={6} md={6}>

            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8} md={8}>
                  <ButtonInc
                    variant="contained"
                    color="primary"
                    size="large"
                    type='submit'
                    disabled={nothingToRaise}
                    style={{ marginTop: "20px", float: "right" }}
                  >
                    Save details
                  </ButtonInc>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                  <ButtonInc
                    variant="contained"
                    color="primary"
                    size="large"
                    style={{ marginTop: "20px" }}
                    onClick={props.toggleClaimExpenseDialog}
                  >
                    Cancel
                  </ButtonInc>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ValidatorForm>
      </ModalInc>

    </>
  )
}

export default AddFuneralClaimExpense